import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldCheck, AlertTriangle, Wand2, ArrowRightCircle, FileWarning, Search } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("it-IT");
  } catch { return iso; }
};

const KindBadge = ({ kind }) => {
  if (kind === "lead") {
    return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">LEAD</span>;
  }
  return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">CLIENTE</span>;
};

const ClientRow = ({ c, suggested, onFix, onMerge }) => (
  <div className="flex items-start flex-wrap gap-3 p-3 bg-white border border-slate-200 rounded-md hover:shadow-sm transition-shadow" data-testid={`cf-row-${c.id}`}>
    <div className="flex-1 min-w-[240px]">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-slate-900">{c.surname} {c.name}</span>
        <KindBadge kind={c.client_kind} />
        {c.created_at && <span className="text-[10px] text-slate-500">creato il {formatDate(c.created_at)}</span>}
      </div>
      <div className="text-xs text-slate-600 mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
        <div><span className="text-slate-400">CF attuale:</span> <span className={`font-mono font-semibold ${(c.fiscal_code || "").length === 16 ? "text-slate-800" : "text-rose-700"}`}>{c.fiscal_code || "—"}</span> <span className="text-[10px] text-slate-400">({(c.fiscal_code || "").length} char)</span></div>
        {c.birth_date && <div><span className="text-slate-400">Nato il:</span> {formatDate(c.birth_date)}</div>}
        {c.email && <div className="truncate"><span className="text-slate-400">Email:</span> {c.email}</div>}
        {(c.mobile || c.phone) && <div><span className="text-slate-400">Tel:</span> {c.mobile || c.phone}</div>}
      </div>
      {suggested && (
        <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs flex items-start gap-2" data-testid={`cf-suggested-${c.id}`}>
          <ArrowRightCircle className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
          <div>
            Suggerimento: il CF completo è probabilmente <span className="font-mono font-bold text-emerald-900">{suggested.fiscal_code}</span> (trovato su <KindBadge kind={suggested.client_kind} /> <strong>{suggested.surname} {suggested.name}</strong>).
            Puoi <button className="underline text-emerald-700 hover:text-emerald-900" onClick={() => onMerge(c, suggested)} data-testid={`cf-merge-btn-${c.id}`}>unire questo record nell&apos;altro</button> o <button className="underline text-emerald-700 hover:text-emerald-900" onClick={() => onFix(c, suggested.fiscal_code)} data-testid={`cf-apply-suggested-${c.id}`}>applicare il CF corretto</button>.
          </div>
        </div>
      )}
    </div>
    <div className="flex flex-col gap-1.5 shrink-0">
      <Button size="sm" variant="outline" onClick={() => onFix(c, null)} data-testid={`cf-fix-btn-${c.id}`}>
        <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Correggi CF
      </Button>
    </div>
  </div>
);

export default function CfCleanup() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("truncated_prefix");
  const [fixDialog, setFixDialog] = useState(null); // {client, suggested_cf}
  const [fixCf, setFixCf] = useState("");
  const [saving, setSaving] = useState(false);
  const [mergeDialog, setMergeDialog] = useState(null); // {source, target}
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients/cf-cleanup/anomalies");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openFix = (c, suggestedCf) => {
    setFixDialog({ client: c });
    setFixCf(suggestedCf || c.fiscal_code || "");
  };

  const submitFix = async () => {
    if (!fixDialog?.client) return;
    setSaving(true);
    try {
      await api.patch(`/clients/${fixDialog.client.id}/fix-fiscal-code`, { fiscal_code: fixCf });
      toast.success("Codice Fiscale corretto");
      setFixDialog(null);
      setFixCf("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openMerge = (src, target) => {
    setMergeDialog({ source: src, target });
  };

  const submitMerge = async () => {
    if (!mergeDialog) return;
    setSaving(true);
    try {
      const r = await api.post(`/clients/${mergeDialog.source.id}/merge-into/${mergeDialog.target.client_id || mergeDialog.target.id}`);
      const moved = r.data?.moved || {};
      const movedSummary = Object.entries(moved).map(([k, v]) => `${v} ${k}`).join(", ") || "nessun record collegato";
      toast.success(`Unione completata (${movedSummary})`);
      setMergeDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const summary = data?.summary || { invalid_length: 0, invalid_checksum: 0, truncated_prefix: 0, missing: 0, total_clients_scanned: 0 };
  const tabs = [
    { key: "truncated_prefix", label: "CF troncati (suggerimento)", count: summary.truncated_prefix, icon: ArrowRightCircle, activeCls: "border-violet-500 bg-violet-50" },
    { key: "invalid_length", label: "Lunghezza errata", count: summary.invalid_length, icon: AlertTriangle, activeCls: "border-amber-500 bg-amber-50" },
    { key: "invalid_checksum", label: "Checksum non valido", count: summary.invalid_checksum, icon: FileWarning, activeCls: "border-rose-500 bg-rose-50" },
    { key: "missing", label: "Mancanti", count: summary.missing, icon: ShieldCheck, activeCls: "border-slate-500 bg-slate-100" },
  ];

  const items = (data?.[activeTab] || []).filter((c) => {
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (
      (c.surname || "").toLowerCase().includes(s) ||
      (c.name || "").toLowerCase().includes(s) ||
      (c.fiscal_code || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="cf-cleanup-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            🧹 Bonifica Codici Fiscali
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Individua e correggi CF non validi, troncati o mancanti nell&apos;archivio clienti e lead.
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" data-testid="cf-reload-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Ricarica scansione
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`text-left p-3 rounded-md border-2 transition-all ${activeTab === t.key
              ? `${t.activeCls} shadow-md`
              : "border-slate-200 bg-white hover:border-slate-300"}`}
            data-testid={`cf-tab-${t.key}`}
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-slate-600">
              <t.icon className="h-4 w-4" />
              {t.label}
            </div>
            <div className="text-2xl font-bold mt-1 text-slate-900">{t.count}</div>
          </button>
        ))}
        <div className="p-3 rounded-md border border-slate-200 bg-slate-50/70">
          <div className="text-xs uppercase tracking-wide font-semibold text-slate-600">Totale scansionati</div>
          <div className="text-2xl font-bold mt-1 text-slate-900">{summary.total_clients_scanned}</div>
        </div>
      </div>

      <div className="mb-3 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="cf-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filtra per cognome, nome, CF o email..."
          className="pl-9"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          Scansione in corso...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-emerald-50/30 border border-emerald-200 rounded-md">
          <ShieldCheck className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <p className="text-emerald-800 font-medium">Nessuna anomalia in questa categoria.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="cf-anomalies-list">
          {items.map((c) => (
            <ClientRow
              key={c.id}
              c={c}
              suggested={c.suggested_match}
              onFix={openFix}
              onMerge={openMerge}
            />
          ))}
        </div>
      )}

      {/* Fix CF dialog */}
      <Dialog open={!!fixDialog} onOpenChange={(o) => { if (!o) setFixDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="cf-fix-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5" /> Correggi Codice Fiscale</DialogTitle>
          </DialogHeader>
          {fixDialog?.client && (
            <div className="space-y-3 text-sm">
              <p>
                Cliente: <strong>{fixDialog.client.surname} {fixDialog.client.name}</strong>{" "}
                <KindBadge kind={fixDialog.client.client_kind} />
              </p>
              <p className="text-xs">
                CF attuale: <span className="font-mono font-semibold text-rose-700">{fixDialog.client.fiscal_code || "—"}</span>
              </p>
              <div>
                <Label htmlFor="cf-fix-input">Nuovo Codice Fiscale (16 caratteri)</Label>
                <Input
                  id="cf-fix-input"
                  data-testid="cf-fix-input"
                  value={fixCf}
                  onChange={(e) => setFixCf(e.target.value.toUpperCase().replace(/\s+/g, "").slice(0, 16))}
                  className="font-mono uppercase tracking-wider mt-1"
                  maxLength={16}
                  placeholder="es. RSSMRA80A01H501U"
                />
                <p className="text-[11px] text-slate-500 mt-1">{fixCf.length}/16 caratteri — verrà validato dal backend</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFixDialog(null)} disabled={saving} data-testid="cf-fix-cancel">Annulla</Button>
            <Button onClick={submitFix} disabled={saving || fixCf.length !== 16} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cf-fix-confirm">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salva CF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      <Dialog open={!!mergeDialog} onOpenChange={(o) => { if (!o) setMergeDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="cf-merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightCircle className="h-5 w-5 text-violet-700" /> Unisci cliente duplicato</DialogTitle>
          </DialogHeader>
          {mergeDialog && (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-rose-50 border border-rose-200 rounded">
                <p className="font-semibold text-rose-900 text-xs uppercase mb-1">Da eliminare (sorgente)</p>
                <p><strong>{mergeDialog.source.surname} {mergeDialog.source.name}</strong> <KindBadge kind={mergeDialog.source.client_kind} /></p>
                <p className="font-mono text-xs">CF: {mergeDialog.source.fiscal_code || "—"}</p>
              </div>
              <div className="text-center text-2xl">↓</div>
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                <p className="font-semibold text-emerald-900 text-xs uppercase mb-1">Mantenuto (target)</p>
                <p><strong>{mergeDialog.target.surname} {mergeDialog.target.name}</strong> <KindBadge kind={mergeDialog.target.client_kind} /></p>
                <p className="font-mono text-xs">CF: {mergeDialog.target.fiscal_code}</p>
              </div>
              <p className="text-[11px] text-slate-600 italic">
                ℹ Tutte le polizze, preventivi, offerte, allegati, pagamenti e messaggi del cliente sorgente verranno trasferiti al target. Il cliente sorgente verrà poi eliminato. Operazione non reversibile (verrà registrata in audit log).
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMergeDialog(null)} disabled={saving} data-testid="cf-merge-cancel">Annulla</Button>
            <Button onClick={submitMerge} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="cf-merge-confirm">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Conferma unione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
